import { DashboardSpecVersions } from '../dashboard-spec-versions';
import { DashboardResource } from '../model/dashboard';
import { migrators } from './dashboard-migrators';
import { PanelDefinition } from '@/model';

validateMigrators(migrators);
const versions = Object.keys(migrators).map(Number).sort();
export const CURRENT_SPEC_VERSION = `${versions[versions.length - 1]}`;

export type DashboardMigrators = Record<string, DashboardSpecMigrator<DashboardSpecVersions, DashboardSpecVersions>>;
export interface DashboardSpecMigrator<PrevSpec extends DashboardSpecVersions, NextSpec extends DashboardSpecVersions> {
  version: string; // should be the next spec version
  migrate: (prevSpec: PrevSpec) => NextSpec;
  migratePanel?: (prevPanel: PrevSpec['panels'][string]) => NextSpec['panels'][string];
}

export interface DashboardSpecMigrationError {
  version: string; // use a simple number, and increment by 1 with each new version
  message: string;
}

export function isUpgradeRequired(targetVersion: string, currentVersion?: string) {
  // If currentVersion is undefined, it indicates that the version is so old that it predates the setting of dashboard.spec_version.
  if (!currentVersion) return true;

  return parseInt(currentVersion) < parseInt(targetVersion);
}

/**
 * Validates the migrators to ensure each key matches the migrator version and checks for duplicate versions
 */
export function validateMigrators(migrators: DashboardMigrators) {
  const versionsSeen = new Set<string>();

  for (const versionKey in migrators) {
    const migrator = migrators[versionKey];
    if (!migrator) {
      throw new Error(`Migrator for version ${versionKey} is not defined.`);
    }

    // check if version is a number
    const isNumber = /^\d*$/.test(migrator.version);
    if (!isNumber) {
      throw new Error(`Invalid version format: ${migrator.version}`);
    }

    // check for duplicate versions
    if (versionsSeen.has(migrator.version)) {
      throw new Error(`Duplicate migrator version: ${migrator.version}.`);
    } else {
      versionsSeen.add(migrator.version);
    }

    // check if the version in migrator matches the version key
    if (migrator.version !== versionKey) {
      throw new Error(`Migrator.version ${migrator.version} does not match version key ${versionKey}.`);
    }
  }

  return true;
}

/**
 * Upgrades old dashboard spec to account for breaking changes to the
 * spec.
 */
export function migrateDashboardSpec(dashboard: unknown): {
  dashboard: DashboardResource | undefined;
  errors?: DashboardSpecMigrationError[];
} {
  if (dashboard === undefined) return { dashboard: undefined, errors: [] };

  const modifiedDashboard = JSON.parse(JSON.stringify(dashboard));

  // Validate migrators
  const isValid = validateMigrators(migrators);
  if (!isValid) {
    return { dashboard: undefined, errors: [] };
  }

  const errors: DashboardSpecMigrationError[] = [];
  // Run all migrators from oldest version to newest to ensure we can always upgrade from any version
  const sortedVersions = Object.keys(migrators).map(Number).sort();
  for (const version of sortedVersions) {
    const migrator = migrators[version.toString()];
    if (migrator && isUpgradeRequired(migrator.version, modifiedDashboard.spec_version)) {
      try {
        if (migrator) {
          const migratedPanels = Object.entries(modifiedDashboard.spec.panels ?? {}).reduce(
            (panels, [key, panel]) => {
              return {
                ...panels,
                [key]: migrator.migratePanel?.(panel) ?? panel,
              };
            },
            {} as Record<string, PanelDefinition>
          );

          modifiedDashboard.spec = migrator.migrate(modifiedDashboard.spec);
          modifiedDashboard.spec.panels = migratedPanels;
          modifiedDashboard.spec_version = migrator.version;
        }
      } catch (e) {
        errors.push({ version: migrator.version, message: (e as Error).message });
      }
    }
  }

  return {
    dashboard: modifiedDashboard,
    errors,
  };
}

export function migratePanel(
  panel: DashboardSpecVersions['panels'][string],
  currentVersion: string
): { panel: PanelDefinition; schemaVersion: string; errors: DashboardSpecMigrationError[] } {
  // Validate migrators
  const isValid = validateMigrators(migrators);
  if (!isValid) {
    return { panel, errors: [], schemaVersion: currentVersion };
  }

  let modifiedPanel = JSON.parse(JSON.stringify(panel));

  const errors: DashboardSpecMigrationError[] = [];
  let upgradedVersion = currentVersion;
  // Run all migrators from oldest version to newest to ensure we can always upgrade from any version
  const sortedVersions = Object.keys(migrators).map(Number).sort();
  for (const version of sortedVersions) {
    const migrator = migrators[version.toString()];
    if (migrator && isUpgradeRequired(migrator.version, upgradedVersion)) {
      try {
        if (migrator) {
          modifiedPanel = migrator.migratePanel?.(modifiedPanel) ?? modifiedPanel;
          upgradedVersion = migrator.version;
        }
      } catch (e) {
        errors.push({ version: migrator.version, message: (e as Error).message });
      }
    }
  }

  return {
    panel: modifiedPanel,
    schemaVersion: upgradedVersion,
    errors,
  };
}
